import { Box, Button } from "@material-ui/core";
import AddIcon from "@material-ui/icons/Add";
import EditIcon from "@material-ui/icons/Edit";
import { FreeTrialDialog } from "components/shared/free-trial-dialog";
import { useState } from "react";
import ReactPlayer from "react-player";
import { useSelector } from "react-redux";
import {
  FacebookEmbed,
  InstagramEmbed,
  PinterestEmbed,
  TikTokEmbed,
  XEmbed,
} from "react-social-media-embed";
import { GetConfigData } from "store/config/selectors";
import { GetUrlBuilderData } from "store/url-builder";
import {
  getLinkedInEmbedUrl,
  isFacebookReel,
  isFacebookVideo,
  isInstagram,
  isLinkedIn,
  isPinterest,
  isTikTok,
  isTwitter,
} from "utils";
import { getVideoUrl } from "utils/pathBuilder";
import style from "../social-network/social-network.module.scss";
import { AddVideoDialog } from "./add-video-dialog";

export interface VideoUploadProps {
  builderType: string;
  isFree?: boolean | null;
  duplicateId?: string;
  initialVideo?: { actionType: any; file?: any; videoUrl?: string };
  readonly?: boolean;
}

export const VideoUpload = ({
  builderType,
  isFree = null,
  duplicateId,
  initialVideo,
  readonly = false,
}: VideoUploadProps) => {
  const [openDialog, setOpenDialog] = useState(false);
  const [freeTrialDialog, setFreeTrialDialog] = useState(false);
  const builderData = useSelector(GetUrlBuilderData);
  const confData = useSelector(GetConfigData);

  // Use duplicate section video if provided, otherwise use the main video
  const videoData =
    duplicateId && initialVideo ? initialVideo : builderData.video;
  const videoUrl = getVideoUrl(videoData, confData);
  const isInstagramUrl = videoUrl && isInstagram(videoUrl);
  const isFacebookReelUrl = videoUrl && isFacebookReel(videoUrl);
  const isFacebookVideoUrl = videoUrl && isFacebookVideo(videoUrl);
  const isFacebookUrl = isFacebookReelUrl || isFacebookVideoUrl;
  const isTikTokUrl = videoUrl && isTikTok(videoUrl);
  const isTwitterUrl = videoUrl && isTwitter(videoUrl);
  const isLinkedInUrl = videoUrl && isLinkedIn(videoUrl);
  const isPinterestUrl = videoUrl && isPinterest(videoUrl);
  const isSocialEmbed =
    isInstagramUrl ||
    isTikTokUrl ||
    isTwitterUrl ||
    isLinkedInUrl ||
    isPinterestUrl ||
    isFacebookUrl;

  const openFreeTrialDialog = () => setFreeTrialDialog(true);
  const handleCloseFreeTrialDialog = () => setFreeTrialDialog(false);

  const videoButtonClassName = videoUrl
    ? `${style.videoButtonFloating} ${style.videoButtonHover}`
    : `${style.videoButton} ${style.videoButtonHover}`;
  const videoButtonLabel = videoUrl
    ? isSocialEmbed
      ? "Change Embed"
      : "Change Video"
    : "Add Video";

  return (
    <>
      <Box
        className="designer-video-wrapper"
        sx={{ position: "relative", minHeight: "20em", minWidth: "100%" }}
      >
        {videoUrl &&
          (isInstagramUrl ? (
            <div className="social-embed-preview">
              <InstagramEmbed url={videoUrl} width={328} />
            </div>
          ) : isTikTokUrl ? (
            <div className="social-embed-preview">
              <TikTokEmbed url={videoUrl} width={325} />
            </div>
          ) : isTwitterUrl ? (
            <div className="social-embed-preview social-embed-preview--twitter">
              <XEmbed url={videoUrl} width={325} />
            </div>
          ) : isLinkedInUrl ? (
            <div className="social-embed-preview social-embed-preview--linkedin">
              <iframe
                title="LinkedIn embed"
                src={getLinkedInEmbedUrl(videoUrl)}
                width="100%"
                height="700"
                frameBorder="0"
                allowFullScreen
              />
            </div>
          ) : isPinterestUrl ? (
            <div className="social-embed-preview">
              <PinterestEmbed url={videoUrl} width={325} />
            </div>
          ) : isFacebookUrl ? (
            <div className="social-embed-preview">
              <FacebookEmbed url={videoUrl} width={280} />
            </div>
          ) : (
            <div className="social-embed-wrapper social-embed-wrapper--aspect">
              <ReactPlayer
                className="designer-video-player"
                url={videoUrl}
                controls
                width="100%"
                height="100%"
              />
            </div>
          ))}
        {!readonly && (
          <Button
            onClick={!isFree ? () => setOpenDialog(true) : openFreeTrialDialog}
            className={videoButtonClassName}
          >
            {videoUrl ? <EditIcon /> : <AddIcon />} {videoButtonLabel}
          </Button>
        )}
      </Box>
      {openDialog && (
        <AddVideoDialog
          open={openDialog}
          onClose={() => setOpenDialog(false)}
          builderType={builderType}
          duplicateId={duplicateId}
          initialVideo={initialVideo}
        />
      )}
      {freeTrialDialog && (
        <FreeTrialDialog
          isOpen={freeTrialDialog}
          onClose={handleCloseFreeTrialDialog}
        />
      )}
    </>
  );
};
